import React from "react";
import { Box, Typography, Button } from "@mui/material";
import WifiTetheringIcon from "@mui/icons-material/WifiTethering";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "./SnackbarHandler";

/**
 * A reusable overlay component for displaying and copying quiz codes
 *
 * @param {Object} props - Component props
 * @param {string} props.code - The code to display and copy
 * @param {string} props.quizId - The quiz ID for navigation to results
 * @param {Function} props.onClose - Function to call when closing the overlay
 * @param {Function} props.showSnackbar - Function to display feedback messages
 * @returns {JSX.Element} The code overlay component
 */
const CodeOverlay = ({ code, quizId, onClose }) => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const handleCopyCodeAndClose = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Copy the code to clipboard
    navigator.clipboard
      .writeText(code)
      .then(() => {
        // Show success snackbar
        showSnackbar("Koden kopierad", "success");

        // Close the overlay
        onClose();
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
        showSnackbar("Kunde inte kopiera koden", "error");

        // Still close the overlay even if copy fails
        onClose();
      });
  };

  return (
    <Box
      onClick={handleCopyCodeAndClose}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        cursor: "pointer",
        bgcolor: "rgba(0,0,0,0.9)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          bottom: 30,
        }}
      >
        <Button
          href={`/app/resultat/${quizId}`}
          variant="purp"
          onClick={() => {
            navigate(`/app/resultat/${quizId}`);
          }}
          startIcon={<WifiTetheringIcon />}
        >
          Se resultaten live
        </Button>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: "white",
          position: "absolute",
          bottom: 90,
          mb: 2,
          opacity: 0.7,
          fontWeight: 500,
          fontSize: "1.1rem",
        }}
      >
        1. Eleverna går in på studera.ai/elev och skriver in provnyckeln <br />
        2. Klicka på knappen nedan för att följa deras resultat i realtid
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "white",
          position: "absolute",
          top: 24,
          right: 24,
          opacity: 0.6,
          fontSize: "0.9rem",
        }}
      >
        Klicka var som helst för att kopiera koden
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "white",
          mb: 2,
          opacity: 0.7,
          fontWeight: 400,
          fontSize: "3rem",
        }}
      >
        studera.ai/elev
      </Typography>
      <Typography
        variant="h2"
        sx={{
          color: "white",
          fontWeight: 700,
          letterSpacing: "0.1em",
          fontSize: "7rem",
        }}
      >
        {code}
      </Typography>
    </Box>
  );
};

export default CodeOverlay;
